import { DynamicValue } from "@faedev/dynamic-values";
import { composeClassNames, universalEach } from "@faedev/editor-utils";
import { useRouter } from "next/router";
import { Badge, Button, Jumbotron, Modal } from "react-bootstrap";
import Heading from "src/components/Heading";
import Paragraph from "src/components/Paragraph";
import Text from "src/components/Text";
import Translate from "src/components/Translate";
import constants from "src/constants";
import replaceString from "src/functions/replaceString";
import Small_cancel from "src/icons/Small-cancel";
import { sButton } from "src/styles/sButton.module.scss";
import { sButtonClose } from "src/styles/sButtonClose.module.scss";
import { sButtons } from "src/styles/sButtons.module.scss";
import { sModal } from "src/styles/sModal.module.scss";
import { sModalContacts } from "src/styles/sModalContacts.module.scss";
import { ApiContact } from "src/types/ApiContact";
import { VariantProfileType } from "src/types/VariantProfileType";

type Props = {
  data?: {
    title: string;
    profileType: VariantProfileType;
    contacts: ApiContact[];
  };
  className?: (string | string) | null | undefined;
  openModalContacts: DynamicValue<boolean>;
};

function ModalContacts({ data, className, openModalContacts }: Props) {
  const { locale: lang, push } = useRouter();
  return data ? (
    <Modal
      show={openModalContacts.value}
      className={composeClassNames([
        sModal,
        sModalContacts,
        className,
        "modal-contacts",
      ])}
      size="lg"
      onHide={() => openModalContacts.change(false)}
    >
      <Modal.Header>
        <Modal.Title>{data.title}</Modal.Title>
        <Button
          variant="link"
          onClick={function () {
            return openModalContacts.change(false);
          }}
          size="sm"
          className={composeClassNames([sButtonClose, `btn-close`])}
        >
          <Small_cancel />
        </Button>
      </Modal.Header>
      <Modal.Body>
        {data.contacts.length > 0 ? (
          <div>
            <Paragraph size="md">
              {
                {
                  instructor: (
                    <Translate tKey={`You can contact the instructor using:`} />
                  ),
                  studio: (
                    <Translate tKey={`You can contact the studio using:`} />
                  ),
                  festival: (
                    <Translate tKey={`You can contact the festival using:`} />
                  ),
                }[data.profileType]
              }
            </Paragraph>
            {universalEach(data.contacts, (item, i) => {
              return (
                <Badge pill={true} variant={`light`} key={i}>
                  <Button
                    as="a"
                    type="button"
                    variant="link"
                    target="_blank"
                    href={(function () {
                      switch (item.type) {
                        case `phone`:
                          return `tel:` + item.value;
                        case `email`:
                          return `mailto:` + item.value;
                        case `facebook_messenger`:
                          return `https://m.me/` + item.value;
                        case `facebook_stream`:
                          return `#` + item.value;
                        case `zoom`:
                          return `#` + item.value;
                        case `google_meet`:
                          return `#` + item.value;
                        case `microsoft_skype`:
                          return `skype:` + item.value + `?call`;
                        case `microsoft_teams`:
                          return `#` + item.value;
                        case `instagram`:
                          return item.value;
                        default:
                          return item.type;
                      }
                    })()}
                    className={composeClassNames([sButton])}
                  >
                    <Text width={`regular`} size="xn" className={`regular`}>
                      <Translate
                        tKey={(function () {
                          switch (item.type) {
                            case `phone`:
                              return `phone`;
                            case `email`:
                              return `email`;
                            case `facebook_messenger`:
                              return `Facebook Messenger`;
                            case `facebook_stream`:
                              return `Facebook Stream`;
                            case `zoom`:
                              return `Zoom`;
                            case `google_meet`:
                              return `Google Meet`;
                            case `microsoft_skype`:
                              return `Microsoft Skype`;
                            case `microsoft_teams`:
                              return `Microsoft Teams`;
                            case `instagram`:
                              return `Instagram`;
                            default:
                              return item.type;
                          }
                        })()}
                      />
                      {`:  `}
                    </Text>
                    <Text width={`bold`} size="md" className={`bold`}>
                      {(function () {
                        switch (item.type) {
                          case `phone`:
                            return item.value;
                          case `email`:
                            return item.value;
                          case `facebook_messenger`:
                            return item.value;
                          case `facebook_stream`:
                            return item.value;
                          case `zoom`:
                            return item.value;
                          case `google_meet`:
                            return item.value;
                          case `microsoft_skype`:
                            return item.value;
                          case `microsoft_teams`:
                            return item.value;
                          case `instagram`:
                            return replaceString(
                              replaceString(
                                item.value,
                                constants.Paths.providerInstagram,
                                `@`
                              ),
                              `/`,
                              ``
                            );
                          default:
                            return item.value;
                        }
                      })()}
                    </Text>
                  </Button>
                </Badge>
              );
            })}
          </div>
        ) : null}
        <Jumbotron className="jumbotron-contact">
          <Heading
            type="h2"
            title={
              <Translate
                tKey={`Are you an instructor, yoga studio or festival and would you like to make your online life easier?`}
              />
            }
          />
          <Button
            className={composeClassNames([sButton, "btn-cta"])}
            onClick={() => push(constants.Paths.joinUs)}
            size="lg"
            type="button"
            variant={`outline-light`}
          >
            <Translate tKey={`Join us`} />
          </Button>
        </Jumbotron>
      </Modal.Body>
      <Modal.Footer>
        {constants.Show.contentQuestionnaire ? (
          <>
            <Heading
              type="h4"
              title={
                <Translate
                  tKey={`Help us make the platform useful to other members of the yoga community.`}
                />
              }
            />
            <div
              className={composeClassNames([
                sButtons,
                "buttons",
                "row",
                "align-center",
              ])}
            >
              <Button
                as="a"
                type="button"
                className={composeClassNames([sButton])}
                target="_blank"
                href={constants.Paths.questionnaireInstructors}
                variant="primary"
              >
                <Translate tKey={`Fill in the questionnaire for instructors`} />
              </Button>
              <Button
                as="a"
                type="button"
                className={composeClassNames([sButton])}
                target="_blank"
                variant="primary"
                href={constants.Paths.questionnaireYogis}
              >
                <Translate tKey={`Fill in the questionnaire for yogis`} />
              </Button>
            </div>
          </>
        ) : null}
      </Modal.Footer>
    </Modal>
  ) : null;
}

export default ModalContacts;
